import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { matchingProducts, pexDimension, velocity, simpleSizing, linearLoss, meetsSizing, domesticWater } from './aqua-tools-core.js';

const number = value => new Intl.NumberFormat('pl-PL',{maximumFractionDigits:2}).format(value);
const money = cents => new Intl.NumberFormat('pl-PL',{style:'currency',currency:'PLN'}).format(cents / 100);
const read = (root, selector) => JSON.parse(root.querySelector(selector)?.textContent || '{}');
const numericId = id => String(id || '').split('/').pop();
function node(tag, text) { const e = document.createElement(tag); if (text != null) e.textContent = text; return e; }
function productLink(product, variant, text) {
  const link = node('a',text);
  const url = new URL(product.url,window.location.origin);
  url.searchParams.set('variant',numericId(variant.id));
  link.href = url.pathname + url.search;
  return link;
}

class AquaCompatible extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.catalog = read(this,'[data-aqua-catalog]');
    this.labels = read(this,'[data-aqua-labels]');
    this.current = this.catalog.find(p=>numericId(p.id) === this.dataset.productId);
    this.caps = {};
    this.sequence = 0;
    this.paint(this.dataset.variantId);
    document.addEventListener(StandardEvents.productSelect,event=>this.selection(event),{signal:this.controller.signal});
    fetch(this.dataset.capsUrl,{signal:this.controller.signal}).then(r=>{if(!r.ok)throw Error();return r.json();}).then(data=>{this.caps=data;this.paint(this.variantId);}).catch(()=>{});
  }
  disconnectedCallback() { this.controller?.abort(); super.disconnectedCallback(); }
  async selection(event) {
    if (!this.current || numericId(event.product?.id) !== numericId(this.current.id)) return;
    const sequence = ++this.sequence;
    try {
      const result = await event.promise;
      if (sequence !== this.sequence || !this.isConnected) return;
      this.paint(result.variant?.id);
    } catch {
      if (sequence !== this.sequence || !this.isConnected) return;
      this.querySelector('[data-aqua-results]').replaceChildren(node('p',this.labels.loading_error));
    }
  }
  paint(id) {
    this.variantId = numericId(id);
    const output = this.querySelector('[data-aqua-results]');
    const selected = this.current?.variants.find(v=>numericId(v.id) === this.variantId);
    output.replaceChildren();
    if (!selected) { output.append(node('p',this.labels.choose));return; }
    this.querySelector('[data-aqua-selected]').textContent = this.labels.selected + ': ' + selected.title;
    const matches = matchingProducts(this.catalog,this.current,selected);
    for (const type of ['fitting','alternative']) {
      const card = node('article');card.className='aq-tools-card';card.append(node('h3',this.labels[type]));
      const match = matches[type];
      if (match) {
        const [product,variant]=match;
        card.append(node('p',product.title),node('p',variant.title),node('p',money(variant.price)+' '+this.labels[type==='fitting'?'each':'meter']),productLink(product,variant,this.labels.open));
      } else card.append(node('p',this.labels['no_'+type]));
      output.append(card);
    }
    const system = this.current.title.replace(/^BRUGG\s+/,'').split(' — ')[0];
    const sku = this.caps[system]?.[selected.title];
    const card=node('article');card.className='aq-tools-card';card.append(node('h3',this.labels.cap));
    if(sku)card.append(node('p',sku),node('p',this.labels.cap_pending));
    else card.append(node('p',this.labels.no_cap));
    output.append(card);
  }
}

class AquaCalculator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();this.controller=new AbortController();
    this.catalog=read(this,'[data-aqua-catalog]');this.labels=read(this,'[data-aqua-labels]');
    this.querySelector('form').addEventListener('submit',event=>{event.preventDefault();this.calculate();},{signal:this.controller.signal});
    const form=this.querySelector('form');
    const updatePeople=()=>{
      const enabled=form.elements.dhw.value!=='none';
      this.querySelector('[data-aqua-people]').hidden=!enabled;
      form.elements.people.disabled=!enabled;
      form.elements.people.required=enabled;
      this.querySelector('[data-aqua-calculation]').replaceChildren();
    };
    form.addEventListener('change',updatePeople,{signal:this.controller.signal});
    updatePeople();
  }
  disconnectedCallback(){this.controller?.abort();super.disconnectedCallback();}
  calculate() {
    const form=this.querySelector('form');const data=new FormData(form);
    const result=simpleSizing(Number(data.get('power')),Number(data.get('length')),data.get('source'),data.get('heating'),data.get('dhw'));
    const output=this.querySelector('[data-aqua-calculation]');output.replaceChildren();
    const dhw=domesticWater(data.get('dhw'),Number(data.get('people')));
    if(!result || !dhw){output.append(node('p',this.labels.invalid));return;}
    const layout='DUO';
    if(result.dhwMode!=='none'){
      const water=node('article');water.className='aq-tools-card';
      water.append(node('h3',this.labels.dhw_title),node('p',this.labels.dhw_amount.replace('{people}',number(Number(data.get('people')))).replace('{litres}',number(dhw.litres))),node('p',this.labels.dhw_tank_note),node('p',this.labels[result.dhwMode==='local'?'dhw_local_note':'dhw_remote_note']));
      if(result.dhwMode==='remote')water.append(node('p',this.labels.dhw_remote_limit));
      output.append(water);
    }
    if(result.dhwMode==='remote')output.append(node('p',this.labels.dhw_heating_only));
    output.append(node('h3',this.labels.quantity),node('p',this.labels.quantity_duo.replace('{n}',number(result.one))));
    output.append(node('p',this.labels.quantity_uno.replace('{n}',number(result.one*2)).replace('{one}',number(result.one))));
    if(result.source==='solid_boiler')output.append(node('p',this.labels.boiler_note));
    const assumptions=node('details');assumptions.append(node('summary',this.labels.technical),node('p',this.labels.delta_used+': '+number(result.delta)+'°C'),node('p',this.labels.flow+': '+number(result.flow)+' m³/h'));if(result.dhwMode!=='none')assumptions.append(node('p',this.labels.dhw_energy+': '+number(dhw.energy)+' kWh/dzień'));output.append(assumptions);
    const grid=node('div');grid.className='aq-tools-grid';
    for(const system of ['FLEXSTAR','CALPEX']){
      const product=this.catalog.find(p=>p.title.includes(system+' '+layout));
      const options=(product?.variants||[]).filter(v=>v.available).map(v=>({variant:v,dimension:pexDimension(v.title)})).filter(v=>v.dimension).sort((a,b)=>a.dimension.outer-b.dimension.outer);
      const candidate=options.find(v=>meetsSizing(result,v.dimension));
      const card=node('article');card.className='aq-tools-card';card.append(node('h3',system+' '+layout));
      if(candidate)card.append(node('p',this.labels.candidate+': DUO 2 × '+candidate.dimension.outer+' mm'),node('p',money(candidate.variant.price)+' '+this.labels.meter),productLink(product,candidate.variant,this.labels.check));
      else card.append(node('p',this.labels.none));
      const details=node('details');details.append(node('summary',this.labels.technical));
      if(candidate)details.append(node('p',this.labels.pressure+': '+number(linearLoss(result.flow,candidate.dimension)*2*result.length/1000)+' kPa'));
      const wrap=node('div');wrap.className='aq-calc-table-wrap';const table=node('table');
      const heading=node('tr');for(const text of [this.labels.diameter,this.labels.velocity]){const cell=node('th',text);cell.scope='col';heading.append(cell);}const thead=node('thead');thead.append(heading);table.append(thead);
      const body=node('tbody');for(const option of options){const row=node('tr');row.append(node('td',option.variant.title),node('td',number(velocity(result.flow,option.dimension))));body.append(row);}table.append(body);wrap.append(table);details.append(wrap);card.append(details);grid.append(card);
    }
    output.append(grid);
  }
}
if(!customElements.get('aqua-compatible'))customElements.define('aqua-compatible',AquaCompatible);
if(!customElements.get('aqua-calculator'))customElements.define('aqua-calculator',AquaCalculator);
